import { Fragment, type ReactNode } from 'react';
import clsx from 'clsx';
import { Icon } from '@clay-run/glaze/icon';
import { mentionToken, mentionTokenPattern, type NoteEntry, type NoteMention, type NoteTarget } from '@shared/flow/notes';
import { FLOW_ICONS } from '../../icons';
import type { NotesPeopleFilter } from './flow-notes-store';

/** The contact a note was written on, or null for an account-level note. */
export function entryContactId(entry: Pick<NoteEntry, 'recordKind' | 'recordId'>): string | null {
  return entry.recordKind === 'contact' ? entry.recordId : null;
}

/**
 * The People view filter's predicate; counts and the visible list both use it.
 * A contact thread has nothing to filter, so everything matches there.
 */
export function entryMatchesPeople(
  entry: Pick<NoteEntry, 'recordKind' | 'recordId'>,
  target: Pick<NoteTarget, 'kind'>,
  filter: NotesPeopleFilter,
): boolean {
  if (target.kind === 'contact') return true;
  const contactId = entryContactId(entry);
  if (filter === 'all') return true;
  if (filter === 'account') return contactId === null;
  if (filter === 'contacts') return contactId !== null;
  return contactId === filter.contactId;
}

const CHIP = 'mx-px inline-flex max-w-full items-center gap-1 rounded-md px-1.5 align-baseline text-sm font-medium leading-5';
const CHIP_USER = clsx(CHIP, 'bg-bg-primary text-content-action');
const CHIP_RECORD = clsx(CHIP, 'border border-border-primary bg-bg-primary text-content-primary hover:bg-bg-secondary');

export interface MentionChipHandlers {
  onContactClick: (contactId: string) => void;
  onAccountClick: (accountId: string) => void;
}

/**
 * One inline mention. A teammate is informational text; a contact or account is
 * a button that opens that record's thread in the rail (D10), never a page.
 */
export function MentionChip({ mention, handlers }: { mention: NoteMention; handlers: MentionChipHandlers }) {
  if (mention.kind === 'user') {
    return (
      <span data-mention-kind="user" className={CHIP_USER}>
        {mention.label}
      </span>
    );
  }
  const isAccount = mention.kind === 'account';
  return (
    <button
      type="button"
      data-mention-kind={mention.kind}
      aria-label={`Open ${mention.label}`}
      className={CHIP_RECORD}
      onClick={() => (isAccount ? handlers.onAccountClick(mention.id) : handlers.onContactClick(mention.id))}
    >
      <Icon type={isAccount ? FLOW_ICONS.accounts : FLOW_ICONS.person} size="sm" aria-hidden className="shrink-0 text-content-tertiary" />
      <span className="truncate">{mention.label}</span>
    </button>
  );
}

export interface BodyPart {
  text: string;
  /** Set when `text` is a mention's `@label` token. */
  mention?: NoteMention;
  /** The part's character offset in the body: stable and unique, so usable as a key. */
  offset: number;
}

/**
 * Splits `body` on each mention's `@label` token, by `mentionTokenPattern` (the same rule as
 * extraction and pruning): longest labels win ("@Acme Corp" over "@Acme"), every occurrence
 * matches, a label inside a word or email is not a mention, and a label no longer in the body
 * yields nothing. Shared by the thread bubbles and the composer's tint layer.
 */
export function splitOnMentions(body: string, mentions: NoteMention[]): BodyPart[] {
  if (mentions.length === 0) return [{ text: body, offset: 0 }];
  const byToken = new Map<string, NoteMention>();
  for (const mention of mentions) {
    const token = mentionToken(mention);
    if (!byToken.has(token)) byToken.set(token, mention);
  }
  let offset = 0;
  return body.split(mentionTokenPattern(mentions)).map((text, index) => {
    const part: BodyPart = { text, offset };
    offset += text.length;
    // `split` with one capture group puts the matches at odd indexes.
    const mention = index % 2 === 1 ? byToken.get(text) : undefined;
    return mention ? { ...part, mention } : part;
  });
}

/**
 * Renders `body` with each mention as a chip. The stored body stays plain text
 * (Salesforce history); `mentions` only carries the ids.
 */
export function renderBody(body: string, mentions: NoteMention[], handlers: MentionChipHandlers): ReactNode {
  if (mentions.length === 0) return body;
  return splitOnMentions(body, mentions).map(part =>
    part.mention ? (
      <MentionChip key={part.offset} mention={part.mention} handlers={handlers} />
    ) : (
      <Fragment key={part.offset}>{part.text}</Fragment>
    ),
  );
}
